import type { ComponentProps } from 'react'
import { type VariantProps } from 'tailwind-variants'

import { cn } from '@gto/ui/cn'
import { InputGroup, inputVariants, isAriaInvalid } from '@gto/ui/input'

type TextareaVariantProps = Omit<VariantProps<typeof inputVariants>, 'textAlign'>

export type TextareaProps = Omit<ComponentProps<'textarea'>, 'size'> &
  TextareaVariantProps & {
    /**
     * Caps auto-growth at this many text lines; content beyond scrolls.
     * `rows` sets the minimum height in lines.
     */
    maxRows?: number
  }

// Auto-grows with content where `field-sizing: content` is supported; elsewhere
// it stays at `rows` height and scrolls. `field-sizing` ignores the native
// `rows` minimum, so rows/maxRows are also enforced as lh-based min/max
// heights — vertical padding lives on the shell to keep that math line-exact.
export const Textarea = ({
  className,
  size,
  variant,
  maxRows,
  rows,
  style,
  ...props
}: TextareaProps) => {
  const { input } = inputVariants({ size, variant })

  return (
    <InputGroup
      className={cn('h-auto', size === 'medium' ? 'py-2' : 'py-1', className)}
      invalid={isAriaInvalid(props['aria-invalid'])}
      size={size}
      variant={variant}
    >
      <textarea
        className={cn(input(), 'h-auto resize-none overflow-auto py-0 [field-sizing:content]')}
        rows={rows}
        style={{
          ...(rows === undefined ? null : { minHeight: `${rows}lh` }),
          ...(maxRows === undefined ? null : { maxHeight: `${maxRows}lh` }),
          ...style,
        }}
        {...props}
      />
    </InputGroup>
  )
}
